import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Plus, MoreVertical, MessageSquare, CheckCircle2, Calendar, Loader2, Search, ArrowLeft, ArrowRight, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { PRIORITY, CATEGORY, initials, fmtDate, isOverdue, AgingBadge, labelColor, Dot, STAGE_COLORS, stageColor } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TaskDrawer from "@/components/TaskDrawer";
import CreateTaskDialog from "@/components/CreateTaskDialog";
import { AvatarStack, assigneeIds } from "@/components/Assignees";
import { Switch } from "@/components/ui/switch";

const MINE_KEY = "tn_board_only_mine";

function TaskCard({ task, users, onOpen, onDragStart }) {
  const overdue = isOverdue(task.due_date, task.category);
  const pct = task.subtasks_total ? Math.round((task.subtasks_done / task.subtasks_total) * 100) : 0;
  return (
    <div draggable onDragStart={(e) => onDragStart(e, task)} onClick={() => onOpen(task.id)}
      data-testid={`kanban-task-card-${task.id}`}
      className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer group mb-2.5 space-y-2.5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-900 leading-snug">{task.title}</p>
        <Badge variant="outline" className={`shrink-0 h-5 px-1.5 text-[10px] font-semibold ${PRIORITY[task.priority].badge}`}>
          {PRIORITY[task.priority].label}
        </Badge>
      </div>
      {task.subtasks_total > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[11px] text-slate-400 font-medium shrink-0">{task.subtasks_done}/{task.subtasks_total}</span>
        </div>
      )}
      {(task.labels?.length > 0 || task.client_name) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {task.client_name && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">{task.client_name}</span>}
          {task.labels?.map((l) => <span key={l} className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${labelColor(l)}`}>{l}</span>)}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-400 flex-wrap">
          <AgingBadge age={task.age_days} inStage={task.days_in_stage} />
          {task.due_date && (
            <span className={`text-[11px] inline-flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : ""}`}>
              <Calendar className="w-3 h-3" />{fmtDate(task.due_date)}
            </span>
          )}
          {task.notes_count > 0 && <span className="text-[11px] inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{task.notes_count}</span>}
        </div>
        <AvatarStack ids={assigneeIds(task)} users={users} testid={`card-assignees-${task.id}`} />
      </div>
    </div>
  );
}

export default function BoardPage() {
  const { boardId } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [board, setBoard] = useState(null);
  const [stages, setStages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState({});
  const [activeTask, setActiveTask] = useState(null);
  const [createStage, setCreateStage] = useState(null);
  const [addingStage, setAddingStage] = useState(false);
  const [newStage, setNewStage] = useState("");
  const [dragOver, setDragOver] = useState(null);
  const [search, setSearch] = useState("");
  const [fPriority, setFPriority] = useState("all");
  const [fLabel, setFLabel] = useState("all");
  const [fAssignee, setFAssignee] = useState("all");
  const [onlyMine, setOnlyMine] = useState(() => localStorage.getItem(MINE_KEY) === "1");
  const toggleMine = (v) => { setOnlyMine(v); localStorage.setItem(MINE_KEY, v ? "1" : "0"); };
  const isManager = user?.role === "manager" || user?.role === "admin";

  const allLabels = Array.from(new Set(tasks.flatMap((t) => t.labels || [])));
  const staff = Object.values(users).filter((u) => u.role !== "client");
  const clientUsers = Object.values(users).filter((u) => u.role === "client");
  const filteredTasks = tasks.filter((t) =>
    (!search || (t.title + " " + (t.description || "")).toLowerCase().includes(search.toLowerCase())) &&
    (fPriority === "all" || t.priority === fPriority) &&
    (fLabel === "all" || (t.labels || []).includes(fLabel)) &&
    (fAssignee === "all" || assigneeIds(t).includes(fAssignee)) &&
    (!onlyMine || assigneeIds(t).includes(user?.id) || (t.subtask_assignee_ids || []).includes(user?.id))
  );

  const load = useCallback(async () => {
    const [b, u] = await Promise.all([api.get(`/boards/${boardId}`), api.get("/users")]);
    setBoard(b.data.board);
    setStages(b.data.stages);
    setTasks(b.data.tasks);
    setUsers(Object.fromEntries(u.data.map((x) => [x.id, x])));
  }, [boardId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = params.get("task");
    if (t) setActiveTask(t);
  }, [params]);

  const openTask = (id) => { setActiveTask(id); setParams({ task: id }); };
  const closeTask = () => { setActiveTask(null); setParams({}); };

  const onDragStart = (e, task) => { e.dataTransfer.setData("taskId", task.id); e.currentTarget.classList.add("kanban-card-dragging"); };
  const onDrop = async (e, stageId) => {
    e.preventDefault();
    setDragOver(null);
    const draggedStage = e.dataTransfer.getData("stageId");
    if (draggedStage) {
      if (draggedStage === stageId) return;
      const from = stages.findIndex((s) => s.id === draggedStage);
      const to = stages.findIndex((s) => s.id === stageId);
      if (from < 0 || to < 0) return;
      const arr = [...stages];
      const [sp] = arr.splice(from, 1);
      arr.splice(to, 0, sp);
      setStages(arr);
      await api.post(`/boards/${boardId}/stages/reorder`, { stage_ids: arr.map((s) => s.id) });
      toast.success("Pipeline reordered");
      load();
      return;
    }
    const taskId = e.dataTransfer.getData("taskId");
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.stage_id === stageId) return;
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, stage_id: stageId } : t));
    try {
      await api.patch(`/tasks/${taskId}`, { stage_id: stageId });
      const stage = stages.find((s) => s.id === stageId);
      toast.success(`Moved to ${stage?.name}`);
      load();
    } catch { toast.error("Could not move task"); load(); }
  };

  const addStage = async () => {
    if (!newStage.trim()) return;
    await api.post(`/boards/${boardId}/stages`, { name: newStage, category: "in_progress" });
    toast.success("Stage added");
    setNewStage(""); setAddingStage(false); load();
  };

  const deleteStage = async (id) => {
    try { await api.delete(`/stages/${id}`); toast.success("Stage deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const setStageColor = async (id, color) => {
    setStages((prev) => prev.map((s) => s.id === id ? { ...s, color } : s));
    await api.patch(`/stages/${id}`, { color });
    toast.success("Stage color updated");
    load();
  };

  const moveStage = async (stageId, dir) => {
    const idx = stages.findIndex((s) => s.id === stageId);
    const ni = idx + dir;
    if (ni < 0 || ni >= stages.length) return;
    const arr = [...stages];
    const [sp] = arr.splice(idx, 1);
    arr.splice(ni, 0, sp);
    setStages(arr);
    await api.post(`/boards/${boardId}/stages/reorder`, { stage_ids: arr.map((s) => s.id) });
    toast.success("Pipeline reordered");
    load();
  };

  if (!board) return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading board…</div>;

  return (
    <div className="flex flex-col h-[calc(100dvh-7.5rem-env(safe-area-inset-bottom))] md:h-screen">
      <div className="h-16 shrink-0 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-20">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold tracking-tight text-slate-900 truncate">{board.name}</h1>
          <p className="text-xs text-slate-500 truncate">{board.description}</p>
        </div>
        <Button onClick={() => setCreateStage(stages[0]?.id)} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="task-create-button">
          <Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">New task</span>
        </Button>
      </div>

      <div className="shrink-0 bg-white/60 border-b border-slate-200 px-4 sm:px-6 py-2.5 flex items-center gap-2 flex-nowrap overflow-x-auto scroll-thin sm:flex-wrap [&>*]:shrink-0" data-testid="board-filter-bar">
        <label className={`h-9 px-3 rounded-md border flex items-center gap-2 text-sm cursor-pointer transition-colors ${onlyMine ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-white border-slate-200 text-slate-600"}`}>
          <Switch checked={onlyMine} onCheckedChange={toggleMine} data-testid="board-only-mine-toggle" />My work only
        </label>
        <div className="relative w-52 sm:w-56">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" className="pl-9 h-9" data-testid="board-search-input" />
        </div>
        <Select value={fAssignee} onValueChange={setFAssignee}>
          <SelectTrigger className="h-9 w-40" data-testid="filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fPriority} onValueChange={setFPriority}>
          <SelectTrigger className="h-9 w-36" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fLabel} onValueChange={setFLabel}>
          <SelectTrigger className="h-9 w-36" data-testid="filter-label"><SelectValue placeholder="Label" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All labels</SelectItem>
            {allLabels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || fPriority !== "all" || fLabel !== "all" || fAssignee !== "all") && (
          <Button variant="ghost" size="sm" className="h-9 text-slate-500" onClick={() => { setSearch(""); setFPriority("all"); setFLabel("all"); setFAssignee("all"); }} data-testid="clear-filters">Clear</Button>
        )}
      </div>

      <div className="flex-1 overflow-x-auto overflow-y-hidden scroll-thin p-4 sm:p-6">
        <div className="flex gap-4 h-full items-start">
          {stages.map((stage) => {
            const colTasks = filteredTasks.filter((t) => t.stage_id === stage.id);
            return (
              <div key={stage.id} data-testid={`kanban-column-${stage.id}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(stage.id); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => onDrop(e, stage.id)}
                className={`w-80 min-w-[320px] max-w-[320px] bg-slate-100/70 border border-slate-200/80 rounded-xl p-3 flex flex-col max-h-full ${dragOver === stage.id ? "kanban-col-over" : ""}`}>
                <div className="h-1 rounded-full mb-2.5" style={{ backgroundColor: stageColor(stage) }} />
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    {isManager && (
                      <span draggable onDragStart={(e) => { e.dataTransfer.setData("stageId", stage.id); }}
                        title="Drag to reorder pipeline"
                        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-indigo-600 transition-colors" data-testid={`drag-stage-${stage.id}`}>
                        <GripVertical className="w-4 h-4" />
                      </span>
                    )}
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stageColor(stage) }} />
                    <h3 className="font-display text-sm font-semibold text-slate-700">{stage.name}</h3>
                    <span className="text-xs text-slate-400 bg-slate-200/70 rounded-full px-1.5">{colTasks.length}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => setCreateStage(stage.id)} className="text-slate-400 hover:text-indigo-600 p-1" data-testid={`add-task-to-${stage.id}`}><Plus className="w-4 h-4" /></button>
                    {isManager && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="text-slate-400 hover:text-slate-600 p-1"><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => moveStage(stage.id, -1)} data-testid={`move-stage-left-${stage.id}`}><ArrowLeft className="w-4 h-4 mr-2" />Move left</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => moveStage(stage.id, 1)} data-testid={`move-stage-right-${stage.id}`}><ArrowRight className="w-4 h-4 mr-2" />Move right</DropdownMenuItem>
                          <div className="px-2 py-1.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Stage color</p>
                            <div className="flex items-center gap-1.5">
                              {STAGE_COLORS.map((c) => (
                                <button key={c} onClick={() => setStageColor(stage.id, c)}
                                  className="w-4 h-4 rounded-full ring-1 ring-black/5 hover:scale-110 transition-transform"
                                  style={{ backgroundColor: c }} data-testid={`stage-color-${stage.id}-${c}`} />
                              ))}
                            </div>
                          </div>
                          <DropdownMenuItem className="text-red-600" onClick={() => deleteStage(stage.id)} data-testid={`delete-stage-${stage.id}`}>Delete stage</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto scroll-thin -mr-1 pr-1 min-h-[80px]">
                  {colTasks.map((t) => <TaskCard key={t.id} task={t} users={users} onOpen={openTask} onDragStart={onDragStart} />)}
                  {colTasks.length === 0 && <div className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-lg">Drop tasks here</div>}
                </div>
              </div>
            );
          })}

          {isManager && (
            <div className="w-72 min-w-[288px] shrink-0">
              {addingStage ? (
                <div className="bg-slate-100/70 border border-slate-200 rounded-xl p-3 space-y-2">
                  <Input autoFocus value={newStage} onChange={(e) => setNewStage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addStage()} placeholder="Stage name" data-testid="new-stage-input" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addStage} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-stage-button">Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddingStage(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAddingStage(true)} data-testid="kanban-add-stage-button"
                  className="w-full text-sm text-slate-500 hover:text-indigo-600 border border-dashed border-slate-300 hover:border-indigo-300 rounded-xl py-3 flex items-center justify-center gap-1.5 transition-colors">
                  <Plus className="w-4 h-4" /> Add stage
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {activeTask && (
        <TaskDrawer taskId={activeTask} users={users} onClose={closeTask} onChanged={load} />
      )}
      {createStage && (
        <CreateTaskDialog boardId={boardId} stageId={createStage} stages={stages} users={staff} clients={clientUsers}
          onClose={() => setCreateStage(null)} onCreated={load} />
      )}
    </div>
  );
}
